// Pure formatting/derivation helpers for the desktop Watchlist redesign.
// No React, no side effects — safe to unit-test and reuse.
//
// Symbol/name display now delegates to the app-wide instrument formatter
// (src/utils/instrumentFormat) so Watchlist, Portfolio, Trade and search all
// render the same strings. These re-exports keep existing imports working.
import { baseSymbol, instrumentName } from "../../utils/instrumentFormat";
export { baseSymbol, pairSymbol, symbolParts, titleCaseInstrument, instrumentName } from "../../utils/instrumentFormat";

// Directional day-change color (platform rule, mirrors the Portfolio screen
// NewPortfolioSection.jsx): positive green, negative red, zero grey.
export const POS = "#35b23c";
export const NEG = "#e23b3b";
export const ZERO = "#8a8b8f";

export function dayChangeColor(value) {
  const v = Number(value);
  if (!Number.isFinite(v) || v === 0) return ZERO;
  return v > 0 ? POS : NEG;
}

// Positive: no plus sign, no arrow. Negative: keep the minus (accessibility).
// Returns null when there is no usable number so the caller can render a dash.
export function formatDayChange(value) {
  if (value === null || value === undefined || value === "") return null;
  const v = Number(value);
  if (!Number.isFinite(v)) return null;
  return `${v < 0 ? "-" : ""}${Math.abs(v).toFixed(2)}%`;
}

// "$1,234.56" — null when there is no usable number (caller renders a dash).
export function formatPrice(value) {
  if (value === null || value === undefined || value === "") return null;
  const v = Number(value);
  if (!Number.isFinite(v)) return null;
  return `$${v.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

// Clean display symbol for the circle chip / ticker preview — BASE asset only.
// Delegates to the shared formatter so crypto/forex resolve consistently:
//   "X:BTCUSD" -> "BTC", "X:BTCAUD" -> "BTC", "C:EURUSD" -> "EUR", "AAPL" -> "AAPL"
export function cleanSymbol(symbol, category) {
  return baseSymbol(symbol, category);
}

// Short asset name for a title line — parenthetical + legal suffix trimmed and
// title-cased via the shared formatter. Keeps the "N/A" fallback callers expect.
export function shortName(name) {
  if (!name || typeof name !== "string") return "N/A";
  return instrumentName({ name }) || "N/A";
}

// Header actions for the main card, derived from list type + active + mode.
// In edit/add mode the card body owns its own header (Done / Close), so no
// title-row buttons are returned.
export function deriveHeaderButtons({ isCurated, isActive, mode }) {
  if (mode !== "view") return [];
  if (isCurated) {
    return isActive
      ? []
      : [{ key: "useInTrade", label: "Use in Trade", variant: "outline" }];
  }
  return isActive
    ? [{ key: "addAssets", label: "Add Assets", variant: "filled" }]
    : [
        { key: "useInTrade", label: "Use in Trade", variant: "outline" },
        { key: "addAssets", label: "Add Assets", variant: "filled" },
      ];
}

// Curated (QUSHi) lists live under parent 5. "The Core" is the app-wide default
// list, so it is always pinned first; everything else keeps the API's order
// (position, then id). Matched by name because The Core's id differs between
// environments. Every surface that lists the curated lists — the Watchlist
// panel, the mobile picker, the Trade screen's dropdown — MUST sort through
// this one helper so the three can never drift out of order.
export const CURATED_PARENT_ID = 5;
export const CORE_LIST_NAME = "the core";

export function sortCuratedLists(lists) {
  if (!Array.isArray(lists)) return [];
  const isCore = (l) => String(l?.name || "").trim().toLowerCase() === CORE_LIST_NAME;
  return [...lists].sort((a, b) => {
    const aCore = isCore(a);
    const bCore = isCore(b);
    return aCore === bCore ? 0 : aCore ? -1 : 1;
  });
}

// The symbol used to open the chart for an item (mirrors legacy SearchPage/Child
// behavior). Kept here so every row uses the same rule.
export function chartSymbolFor(item) {
  return item?.is_system && item?.etf ? item.etf : item?.symbol;
}
